import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, ShieldCheck, Sparkles, Workflow, Clock, Wallet,
  Users, Zap, LineChart, Globe2, Lock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TESTIMONIALS } from '@/mock/mockData';

const FEATURES = [
  { icon: Workflow, title: 'Dynamic Workflows', body: 'State-machine checklists that mutate when the path changes — ART appeals, BVE, sponsor swap.' },
  { icon: Sparkles, title: 'AI Copilot Native', body: 'Qualification → occupation → assessing body auto-mapping. RFI drafts. Document triage.' },
  { icon: ShieldCheck, title: 'OMARA-First Compliance', body: 'Client trust ledger, three-way recon, pre-transfer consent gate, 7-year retention.' },
  { icon: Globe2, title: 'Timeline Client Portal', body: 'Applicants see status, next steps, ETA in 12 languages — not another file dump.' },
  { icon: Zap, title: 'Assessing-Body Sync', body: 'Auto-fetch requirements from VETASSESS, CPA, ACS, EA, AHPRA — refreshed on schedule.' },
  { icon: LineChart, title: 'Practice Analytics', body: 'Grant rate by subclass, ageing WIP, profitability per matter. Benchmark to DHA median.' },
];

const LOGOS = ['NSW Law Society', 'ISO 27001:2022', 'OMARA-aligned', 'AWS Sydney'];

export default function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-[#F4F7FA]" />
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-20">
          <div className="grid gap-16 md:grid-cols-5">
            <div className="md:col-span-3">
              <Badge variant="secondary" className="bg-[#2ECC9A]/10 text-[#2ECC9A] border-[#2ECC9A]/30">
                Built in Australia · OMARA-first · AI-native
              </Badge>
              <h1 className="mt-5 font-serif text-5xl font-bold leading-[1.05] tracking-tight text-[#1A3A5C] md:text-6xl">
                Your new chapter starts here.
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-slate-600">
                Immigration can feel overwhelming — but it doesn't have to be. ImmiPro is the practice operating system for Australian Registered Migration Agents, immigration lawyers and corporate mobility teams: dynamic workflows, an AI Copilot, OMARA-compliant trust accounting, and a transparent client portal. Priced so a solo practitioner can start today.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/demo">
                  <Button className="h-12 gap-2 bg-[#1A3A5C] px-6 text-white hover:bg-[#1A3A5C]/90">
                    Get started
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/app">
                  <Button variant="outline" className="h-12 border-[#1A3A5C]/20 px-6 text-[#1A3A5C] hover:bg-[#1A3A5C]/5">
                    Explore the product
                  </Button>
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" /> 100% web-native</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" /> AU-hosted (Sydney)</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" /> Solo tier from A$49/mo</span>
              </div>
            </div>

            {/* Snapshot card */}
            <div className="md:col-span-2">
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-2xl">
                <div className="rounded-lg bg-[#1A3A5C] p-4 text-white">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-widest text-[#2ECC9A]">Live matter</span>
                    <span className="text-xs text-white/60">NM-2025-0142</span>
                  </div>
                  <div className="mt-2 text-lg font-semibold">John Smith — Subclass 186 (DE)</div>
                  <div className="mt-1 text-xs text-white/60">Occupation → Software Engineer · Assessing body → ACS</div>
                </div>
                <div className="space-y-3 p-3">
                  {[
                    { l: 'Skills assessment — ACS', s: 'Positive', tone: 'positive' },
                    { l: 'English — PTE 79+', s: 'Verified', tone: 'positive' },
                    { l: 'Employment refs (3y)', s: 'Verified', tone: 'positive' },
                    { l: 'Medical exam', s: 'Booked — 14 Aug', tone: 'warn' },
                    { l: 'AFP police check', s: 'Awaiting client', tone: 'warn' },
                  ].map((r) => (
                    <div key={r.l} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                      <span className="text-slate-700">{r.l}</span>
                      <span className={`text-xs font-medium ${r.tone === 'positive' ? 'text-emerald-600' : 'text-amber-600'}`}>{r.s}</span>
                    </div>
                  ))}
                  <div className="rounded-md bg-[#2ECC9A]/10 p-3">
                    <div className="flex items-center gap-2 text-xs font-medium text-[#1A3A5C]">
                      <Sparkles className="h-3.5 w-3.5 text-[#2ECC9A]" /> AI Copilot suggests
                    </div>
                    <div className="mt-1 text-sm text-slate-700">Book medical exam within 7 days to avoid Bupa waitlist — average 22 days in Sydney.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust bar */}
          <div className="mt-16 border-t border-slate-200 pt-8">
            <div className="text-center text-xs uppercase tracking-widest text-slate-500">
              Compliance-aligned with
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
              {LOGOS.map((l) => (
                <span key={l} className="font-serif text-sm font-semibold text-slate-500">{l}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-y border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">The platform</div>
            <h2 className="mt-2 font-serif text-4xl font-bold tracking-tight text-[#1A3A5C]">
              Everything an Australian immigration practice needs — in one place.
            </h2>
            <p className="mt-3 text-slate-600">
              Fifteen modules, one workspace. Replace your CRM, spreadsheet, desktop lodgement tool, trust ledger, and generic client portal with software built by people who read the Regulations.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="border-slate-200 transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#2ECC9A]/10">
                      <Icon className="h-5 w-5 text-[#2ECC9A]" />
                    </div>
                    <div className="mt-5 text-lg font-semibold text-[#1A3A5C]">{f.title}</div>
                    <div className="mt-1 text-sm text-slate-600">{f.body}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Occupation → body mapping showcase */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">Occupation intelligence</div>
            <h2 className="mt-2 font-serif text-4xl font-bold tracking-tight text-[#1A3A5C]">
              Pick the occupation. We pick the assessing body.
            </h2>
            <p className="mt-3 text-slate-600">
              ImmiPro maps ANZSCO to the correct skills-assessing authority (CPA, IPA, CAANZ, VETASSESS, Engineers Australia, ACS, AHPRA, TRA) and auto-loads their current evidence list. When the body changes their requirements, your open matters get flagged.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-slate-700">
              {[
                'ANZSCO 261313 → ACS → 12 evidence items auto-loaded.',
                'ANZSCO 221111 → CPA / IPA / CAANZ (client picks) → evidence checklist.',
                'ANZSCO 351311 → VETASSESS → trade-specific pathway.',
                'Body updates their list → Policy Pulse flags your open matters.',
              ].map((li) => (
                <li key={li} className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2ECC9A]" />
                  {li}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-lg">
            <div className="text-xs uppercase tracking-widest text-slate-500">Auto-mapping demo</div>
            <div className="mt-4 space-y-3">
              {[
                { occ: 'Software Engineer', code: '261313', body: 'ACS', tone: 'ACS' },
                { occ: 'Accountant (General)', code: '221111', body: 'CPA / IPA / CAANZ', tone: 'CPA' },
                { occ: 'Chef', code: '351311', body: 'VETASSESS', tone: 'VET' },
                { occ: 'Registered Nurse', code: '254411', body: 'AHPRA', tone: 'AH' },
                { occ: 'Electrician', code: '341111', body: 'TRA', tone: 'TRA' },
              ].map((r) => (
                <div key={r.code} className="flex items-center justify-between rounded-md border border-slate-100 bg-[#F4F7FA] p-3">
                  <div>
                    <div className="text-sm font-medium text-[#1A3A5C]">{r.occ}</div>
                    <div className="text-xs text-slate-500">ANZSCO {r.code}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <span className="rounded-md bg-[#1A3A5C] px-2.5 py-1 text-xs font-medium text-[#2ECC9A]">{r.body}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-y border-slate-200 bg-[#1A3A5C] text-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">What practitioners say</div>
          <h2 className="mt-2 max-w-3xl font-serif text-4xl font-bold tracking-tight">
            From solo RMAs to Big-4 mobility teams.
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-lg border border-white/10 bg-white/5 p-6">
                <div className="font-serif text-lg leading-snug text-white">&ldquo;{t.quote}&rdquo;</div>
                <div className="mt-5 text-sm font-medium">{t.name}</div>
                <div className="text-xs text-white/60">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-serif text-4xl font-bold tracking-tight text-[#1A3A5C]">
              See ImmiPro in your workflow.
            </h2>
            <p className="mt-3 max-w-xl text-slate-600">
              A 30-minute demo tailored to your practice size and visa mix. We&rsquo;ll bring a live matter walkthrough, the OMARA compliance map, and a data-migration checklist.
            </p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <Link to="/demo">
              <Button className="h-12 gap-2 bg-[#1A3A5C] px-6 text-white hover:bg-[#1A3A5C]/90">
                Book a demo <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/pricing" className="text-sm font-medium text-slate-600 hover:text-[#1A3A5C]">
              or see pricing →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
